import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../lib/supabase';
import { Card } from '../components/ui/card';
import { Heart, Activity, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function OwnerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/owner/login');
      return;
    }
    if (user.role !== 'owner') {
      navigate('/clinic/dashboard');
      return;
    }
    fetchPets();
  }, [user, navigate]);

  const fetchPets = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_URL}/api/owner/pets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPets(response.data);
    } catch (error) {
      toast.error('Error al cargar tus mascotas');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Recibido': 'bg-slate-100 text-slate-700',
      'En evaluación': 'bg-amber-100 text-amber-700',
      'En procedimiento': 'bg-amber-100 text-amber-700',
      'En recuperación': 'bg-green-100 text-green-700',
      'Listo para recoger': 'bg-green-100 text-green-700',
      'Cerrado': 'bg-slate-100 text-slate-500'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Cargando tus mascotas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-slate-900">Mis Mascotas</h1>
          </div>
          <p className="text-slate-600">Accede al historial clínico completo de tus mascotas</p>
        </div>

        {pets.length === 0 ? (
          <Card className="p-12 text-center bg-white border border-slate-200">
            <Heart className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              Aún no tienes mascotas vinculadas
            </h3>
            <p className="text-slate-600 mb-4">
              Pide a tu clínica veterinaria que te genere un código NFC para vincular a tu mascota
            </p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pets.map((pet) => (
              <Link
                key={pet.id}
                to={`/owner/pets/${pet.id}`}
                className="block"
                data-testid={`pet-card-${pet.id}`}
              >
                <Card className="p-6 bg-white border border-slate-200 hover:shadow-lg transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center">
                      <Heart className="h-6 w-6 text-blue-600" />
                    </div>
                    {pet.active_case && (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(pet.active_case.status)}`}>
                        {pet.active_case.status}
                      </span>
                    )}
                  </div>
                  
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{pet.name}</h3>
                  <p className="text-slate-600 mb-1">
                    {pet.species} {pet.breed && `• ${pet.breed}`}
                  </p>
                  {pet.age && (
                    <p className="text-sm text-slate-500">{pet.age} años</p>
                  )}
                  
                  {pet.active_case && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <div className="flex items-center gap-2 text-sm text-slate-700">
                        <AlertCircle className="h-4 w-4 text-amber-600" />
                        <span>Caso activo en clínica</span>
                      </div>
                    </div>
                  )}
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
